import { BrowserRouter, Routes, Route, Navigate } from 'react-router'
import { ErrorBoundary } from 'react-error-boundary'
import { requestBuildVersionCheck } from '@klear/ui/hooks/use-build-version-gate'
import AuthLayout from '@/components/AuthLayout'
import PermissionGuard from '@/components/PermissionGuard'
import { AuthProvider } from '@klear/ui/contexts/auth-context'

import Unauthorized from '@/app/Unauthorized'
import NotFound from '@/app/NotFound'
import Traders from '@/app/trader/Traders'
import TraderDetail from '@/app/trader/TraderDetail'
import {
  PERMISSION_ENTITY_TRADER,
  PERMISSION_ACTION_VIEW,
  PERMISSION_ENTITY_USER,
  PERMISSION_ENTITY_CUSTOMER,
  PERMISSION_ENTITY_SUPPLIER,
  PERMISSION_ENTITY_ORDER,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_PAYMENT,
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_COMPLETE,
  PERMISSION_ACTION_EDIT,
  ROUTE_ADMIN,
  ROUTE_COLLECT,
  ROUTE_COLLECTIONS,
  ROUTE_DISBURSE,
  ROUTE_DISBURSEMENTS,
  ROUTE_PAYMENT,
  ROUTE_TRANSFERS,
  ROUTE_TRANSFER,
  PERMISSION_ACTION_FUND,
  ROUTE_ORDERS,
  ROUTE_ORDER_EVENTS,
  ROUTE_CUSTOMERS,
  ROUTE_SUPPLIERS,
  ROUTE_USER,
  ROUTE_TRADER,
  ROUTE_SPV_UTILIZATION,
  ROUTE_DOCUMENTS,
  ROUTE_DOCUMENT_UPLOAD,
  ROUTE_DOCUMENT_REVIEW,
  ROUTE_RECEIVABLES,
  PERMISSION_ENTITY_DOCUMENTS,
  PERMISSION_ENTITY_SOURCE_DOCUMENT,
  ROUTE_FUNDER_PRICING,
  ROUTE_REPORTS,
  ROUTE_REPORTS_PAYMENTS,
  ROUTE_REPORTS_FUNDING,
  PERMISSION_ENTITY_FUNDER
} from '@klear/constants'
import Users from '@/app/user/Users'
import UserView from '@/app/user/UserView'
import UserAdd from '@/app/user/UserAdd'
import UserEdit from '@/app/user/UserEdit'
import { Toaster } from '@klear/ui/components/toaster'
import { getAppBasePath } from '@klear/runtime-env'
import { redirectToLogin } from '@klear/ui/lib/auth-redirect'
import Suppliers from '@/app/supplier/Suppliers'
import SupplierWizardPage from '@/app/supplier/SupplierWizardPage'
import Customers from '@/app/customer/Customers'
import CustomerWizardPage from '@/app/customer/CustomerWizardPage'
import Dashboard from '@/app/Dashboard'
import OrderEventDetail from '@/app/order/OrderEventDetail'
import OrderIdDisplayRedirect from '@/app/order/OrderIdDisplayRedirect'
import PaymentDetail from '@/app/payments/PaymentDetail'
import Transfers from '@/app/transfers/Transfers'
import TransferDetail from '@/app/transfers/TransferDetail'
import CreateTransfer from '@/app/transfers/CreateTransfer'
import CollectionWizard from '@/app/collections/CollectionWizard'
import Collections from '@/app/collections/Collections'
import DisbursementWizard from '@/app/disbursements/DisbursementWizard'
import Disbursements from '@/app/disbursements/Disbursements'
import FundingReport from '@/app/reports/FundingReport'
import PaymentsReport from '@/app/reports/PaymentsReport'
import Reports from '@/app/reports/Reports'
import SpvUtlizationReport from '@/app/reports/spv-utlization-report'
import Documents from '@/app/documents/Documents'
import DocumentTriageRedirect from '@/app/document-review/DocumentTriageRedirect'
import Receivables from '@/app/receivables/Receivables'
import ReceivablesOffer from '@/app/receivables/ReceivablesOffer'
import DocumentReviewDetail from '@/app/document-review/DocumentReviewDetail'
import DocumentReviewNotProcessed from '@/app/document-review/DocumentReviewNotProcessed'
import { DocumentUploadPage } from '@klear/ui/components/document-upload-page'
import FunderPricing from '@/app/funder-pricing/FunderPricing'

const APP_BASE = getAppBasePath(ROUTE_ADMIN)

function ErrorFallback({ error }: { error: Error }) {
  if (error.message === 'Unauthorized') {
    redirectToLogin()
    return null
  }

  return (
    <div>
      <h2>Something went wrong</h2>
      <pre>{error.message}</pre>
      <pre>{error.stack}</pre>
    </div>
  )
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/" element={<AuthLayout />}>
        <Route index element={<Dashboard />} />
        <Route
          path={ROUTE_TRADER}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_TRADER} action={PERMISSION_ACTION_VIEW}>
              <Traders />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_TRADER}/:traderId`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_TRADER} action={PERMISSION_ACTION_VIEW}>
              <TraderDetail />
            </PermissionGuard>
          }
        />
        <Route path={ROUTE_ORDERS} element={<Navigate to={`/${ROUTE_RECEIVABLES}`} replace />} />
        <Route
          path={`${ROUTE_ORDERS}/:idDisplay`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_ORDER} action={PERMISSION_ACTION_VIEW}>
              <OrderIdDisplayRedirect appPrefix={APP_BASE} />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_ORDERS}/:idDisplay/${PERMISSION_ACTION_EDIT}`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_ORDER} action={PERMISSION_ACTION_EDIT}>
              <OrderIdDisplayRedirect appPrefix={APP_BASE} isEditing />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_ORDER_EVENTS}/:eventId`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_ORDER} action={PERMISSION_ACTION_VIEW}>
              <OrderEventDetail appPrefix={APP_BASE} />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_ORDER_EVENTS}/:eventId/${PERMISSION_ACTION_EDIT}`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_ORDER} action={PERMISSION_ACTION_EDIT}>
              <OrderEventDetail isEditing={true} appPrefix={APP_BASE} />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_TRANSFERS}`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_PAYMENT} action={PERMISSION_ACTION_VIEW}>
              <Transfers />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_TRANSFERS}/${ROUTE_TRANSFER}`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_PAYMENT} action={PERMISSION_ACTION_COMPLETE}>
              <CreateTransfer />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_TRANSFERS}/:paymentId`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_PAYMENT} action={PERMISSION_ACTION_VIEW}>
              <TransferDetail />
            </PermissionGuard>
          }
        />
        <Route
          path={ROUTE_COLLECTIONS}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_INVOICE_OFFER} action={PERMISSION_ACTION_VIEW}>
              <Collections />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_COLLECTIONS}/${ROUTE_COLLECT}/:stepId`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_INVOICE_OFFER} action={PERMISSION_ACTION_FUND}>
              <CollectionWizard />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_COLLECTIONS}/${ROUTE_COLLECT}`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_INVOICE_OFFER} action={PERMISSION_ACTION_FUND}>
              <CollectionWizard />
            </PermissionGuard>
          }
        />
        <Route
          path={ROUTE_DISBURSEMENTS}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_INVOICE_OFFER} action={PERMISSION_ACTION_VIEW}>
              <Disbursements />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_DISBURSEMENTS}/${ROUTE_DISBURSE}/:stepId`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_INVOICE_OFFER} action={PERMISSION_ACTION_FUND}>
              <DisbursementWizard />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_DISBURSEMENTS}/${ROUTE_DISBURSE}`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_INVOICE_OFFER} action={PERMISSION_ACTION_FUND}>
              <DisbursementWizard />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_PAYMENT}/:paymentId`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_INVOICE_OFFER} action={PERMISSION_ACTION_VIEW}>
              <PaymentDetail />
            </PermissionGuard>
          }
        />
        <Route
          path={ROUTE_CUSTOMERS}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_CUSTOMER} action={PERMISSION_ACTION_VIEW}>
              <Customers />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_CUSTOMERS}/new`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_CUSTOMER} action={PERMISSION_ACTION_ADD}>
              <CustomerWizardPage mode="create" />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_CUSTOMERS}/:customerId`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_CUSTOMER} action={PERMISSION_ACTION_VIEW}>
              <CustomerWizardPage mode="view" />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_CUSTOMERS}/:customerId/${PERMISSION_ACTION_EDIT}`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_CUSTOMER} action={PERMISSION_ACTION_EDIT}>
              <CustomerWizardPage mode="edit" />
            </PermissionGuard>
          }
        />
        <Route
          path={ROUTE_SUPPLIERS}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_SUPPLIER} action={PERMISSION_ACTION_VIEW}>
              <Suppliers />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_SUPPLIERS}/new`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_SUPPLIER} action={PERMISSION_ACTION_ADD}>
              <SupplierWizardPage mode="create" />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_SUPPLIERS}/:supplierId`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_SUPPLIER} action={PERMISSION_ACTION_VIEW}>
              <SupplierWizardPage mode="view" />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_SUPPLIERS}/:supplierId/${PERMISSION_ACTION_EDIT}`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_SUPPLIER} action={PERMISSION_ACTION_EDIT}>
              <SupplierWizardPage mode="edit" />
            </PermissionGuard>
          }
        />
        <Route
          path={ROUTE_USER}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_USER} action={PERMISSION_ACTION_VIEW}>
              <Users />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_USER}/new`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_USER} action={PERMISSION_ACTION_VIEW}>
              <UserAdd />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_USER}/:userId`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_USER} action={PERMISSION_ACTION_VIEW}>
              <UserView />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_USER}/:userId/${PERMISSION_ACTION_EDIT}`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_USER} action={PERMISSION_ACTION_VIEW}>
              <UserEdit />
            </PermissionGuard>
          }
        />
        <Route
          path={ROUTE_DOCUMENTS}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_DOCUMENTS} action={PERMISSION_ACTION_VIEW}>
              <Documents />
            </PermissionGuard>
          }
        />
        <Route path={ROUTE_DOCUMENT_REVIEW} element={<DocumentTriageRedirect />} />
        <Route
          path={`${ROUTE_DOCUMENT_REVIEW}/document/:documentKey`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_SOURCE_DOCUMENT} action={PERMISSION_ACTION_VIEW}>
              <DocumentReviewNotProcessed />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_DOCUMENT_REVIEW}/:processingId`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_SOURCE_DOCUMENT} action={PERMISSION_ACTION_VIEW}>
              <DocumentReviewDetail />
            </PermissionGuard>
          }
        />
        <Route
          path={ROUTE_RECEIVABLES}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_INVOICE_OFFER} action={PERMISSION_ACTION_VIEW}>
              <Receivables />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_RECEIVABLES}/:offerId`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_INVOICE_OFFER} action={PERMISSION_ACTION_VIEW}>
              <ReceivablesOffer />
            </PermissionGuard>
          }
        />
        <Route
          path={ROUTE_DOCUMENT_UPLOAD}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_SOURCE_DOCUMENT} action={PERMISSION_ACTION_ADD}>
              <DocumentUploadPage />
            </PermissionGuard>
          }
        />
        <Route
          path={`${ROUTE_SPV_UTILIZATION}`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_INVOICE_OFFER} action={PERMISSION_ACTION_VIEW}>
              <SpvUtlizationReport />
            </PermissionGuard>
          }
        />
        <Route path={ROUTE_REPORTS} element={<Reports />} />
        <Route path={`${ROUTE_REPORTS}/${ROUTE_REPORTS_PAYMENTS}`} element={<PaymentsReport />} />
        <Route
          path={`${ROUTE_REPORTS}/${ROUTE_REPORTS_FUNDING}`}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_INVOICE_OFFER} action={PERMISSION_ACTION_VIEW}>
              <FundingReport />
            </PermissionGuard>
          }
        />
        <Route
          path={ROUTE_FUNDER_PRICING}
          element={
            <PermissionGuard entity={PERMISSION_ENTITY_FUNDER} action={PERMISSION_ACTION_VIEW}>
              <FunderPricing />
            </PermissionGuard>
          }
        />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

function App() {
  return (
    <BrowserRouter basename={APP_BASE}>
      <ErrorBoundary FallbackComponent={ErrorFallback} onError={requestBuildVersionCheck}>
        <AuthProvider>
          <AppRoutes />
          <Toaster />
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  )
}

export default App
